import React, { useContext } from "react";
import { Grid, IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";
import assert from "assert";
import { rowMargin, TicketProps } from "../../FlightPackage/Ticket/FlightLegs/ticketFlightLegs";
import LocationEditor from "../inputs/locationEditor";
import StringEditor from "../inputs/stringEditor";
import TimeEditor from "../inputs/timeEditor";
import { formatNumericInput } from "../../Utils/UtilityMethods";
import { TicketLocationExtended } from "../../Utils/TypeExtensions";
import TimeCalculator from "../../sharedCode/TimeCalculator";
import { GlobalContext } from "../../Utils/Contexts";
import { saveTicketChanges } from "../../Utils/TicketDatabase";
import { FIELD_LIMITS } from "../../sharedCode/fieldLimits";

interface Props extends TicketProps {
    id: number;
    handleDelete: (legToDelete: number) => void;
    mergeRowChanges: (locationId: number, changes: Record<string, unknown>) => void;
}

export default function UpDownRow(props: Props): JSX.Element {
    const context = useContext(GlobalContext);
    const flightLeg = props.ticket.Locations?.find((l) => l?.id === props.id);
    // TODO is this usage of assert appropriate? read up on it more
    assert(flightLeg);
    return (
        <Grid key={flightLeg.id + "r"} container flexDirection={"row"} alignItems={"center"}>
            <Grid key={flightLeg.id + "r1"} item container xs={10} columnSpacing={1} marginTop={rowMargin}>
                <Grid key={`a${props.id}`} item xs={12} xl={6}>
                    <LocationEditor
                        sourceId={flightLeg.id}
                        sourceProperty="location"
                        labelText="LOCATION/LSD - NTS OR DLS"
                        isRequired={true}
                        initialValue={flightLeg.location}
                        mergeChanges={props.mergeRowChanges}
                        isDisabled={props.isReadOnly}
                        errors={props.errors}
                        containerUrl={"ticket/legs"}
                        company_id={props.ticket.company_id}
                        ticketList={props.ticketList}
                    />
                </Grid>
                <Grid key={`b${props.id}`} item xs={12} md={6} xl={2}>
                    <StringEditor
                        key={`cc${flightLeg.location}${flightLeg.id}`}
                        sourceId={flightLeg.id}
                        sourceProperty="cost_code"
                        labelText="COST CODE/AFE"
                        isRequired={true}
                        initialValue={flightLeg.cost_code}
                        mergeChanges={props.mergeRowChanges}
                        isDisabled={props.isReadOnly}
                        errors={props.errors}
                        containerUrl={"ticket/legs"}
                        maxLength={FIELD_LIMITS.cost_code}
                    />
                </Grid>
                <Grid item xs={6} md={3} xl={2}>
                    <Grid key={`tf${flightLeg.id}${"up_time"}`} item xs={12}>
                        <TimeEditor
                            sourceId={flightLeg.id}
                            sourceProperty={"up_time"}
                            labelText={"UP"}
                            isRequired={true}
                            initialValue={flightLeg["up_time" as keyof typeof flightLeg]}
                            mergeChanges={mergeLegTimeChanges}
                            isDisabled={props.isReadOnly}
                            errors={props.errors}
                            containerUrl={"ticket/legs"}
                            documentDate={props.ticket.date}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={6} md={3} xl={2}>
                    <Grid key={`tf${flightLeg.id}${"down_time"}`} item xs={12}>
                        <TimeEditor
                            sourceId={flightLeg.id}
                            sourceProperty={"down_time"}
                            labelText={"DOWN"}
                            isRequired={true}
                            initialValue={flightLeg["down_time" as keyof typeof flightLeg]}
                            mergeChanges={mergeLegTimeChanges}
                            isDisabled={props.isReadOnly}
                            errors={props.errors}
                            containerUrl={"ticket/legs"}
                            documentDate={props.ticket.date}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid key={`e${props.id}`} item container alignItems={"center"} xs={2} spacing={0}>
                <Grid key={`f${props.id}`} item xs={8}>
                    <Typography sx={{ textAlign: "right" }}>{formatNumericInput(flightLeg.hours, 1)}</Typography>
                </Grid>
                <Grid key={`g${props.id}`} item xs={4}>
                    <IconButton
                        disabled={props.isReadOnly}
                        size="large"
                        edge="end"
                        onClick={() => {
                            props.handleDelete(flightLeg?.id);
                        }}>
                        <RemoveIcon />
                    </IconButton>
                </Grid>
            </Grid>
        </Grid>
    );

    function mergeLegTimeChanges(locationId: number, changes: Record<string, unknown>) {
        const locations = props.ticket?.Locations?.map((location) => {
            if (location?.id === locationId) {
                const newRow = { ...location, ...changes } as TicketLocationExtended;
                newRow.hours = TimeCalculator.getDifferenceInHours(newRow.up_time, newRow.down_time);
                newRow.inputHours = formatNumericInput(newRow.hours, 1);
                return newRow;
            } else {
                return location;
            }
        });

        saveTicketChanges(props.ticket.id, { Locations: locations }).catch(() => props.showForceReloadDialogue());
    }
}
